import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  INACTIVITY_TIMEOUT,
  SESSION_WARNING_TIME,
  SESSION_CHECK_INTERVAL,
  isSessionExpired,
  touchSession,
} from '../utils/sessionTimeout.js';

const ACTIVITY_EVENTS = ['click', 'mousemove', 'keydown', 'scroll', 'touchstart', 'touchmove'];

export default function SessionManager({ children, userRole, onLogout, enabled = true }) {
  const [showWarning, setShowWarning] = useState(false);
  const timeoutRef = useRef(null);
  const warningTimeoutRef = useRef(null);
  const intervalRef = useRef(null);
  const onLogoutRef = useRef(onLogout);

  useEffect(() => {
    onLogoutRef.current = onLogout;
  }, [onLogout]);

  const clearTimers = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current);
    if (intervalRef.current) clearInterval(intervalRef.current);
    timeoutRef.current = null;
    warningTimeoutRef.current = null;
    intervalRef.current = null;
  }, []);

  const logout = useCallback(() => {
    clearTimers();
    onLogoutRef.current?.();
  }, [clearTimers]);

  const resetTimer = useCallback(() => {
    if (!enabled) return;
    clearTimers();
    setShowWarning(false);
    touchSession();
    warningTimeoutRef.current = setTimeout(() => {
      // Another tab may have refreshed the timestamp in the meantime.
      if (isSessionExpired(Date.now() + SESSION_WARNING_TIME)) setShowWarning(true);
    }, INACTIVITY_TIMEOUT - SESSION_WARNING_TIME);
    timeoutRef.current = setTimeout(() => {
      if (isSessionExpired()) logout();
    }, INACTIVITY_TIMEOUT);
    // Background timers can be throttled/cleared when the tab or the machine
    // sleeps, so the stored timestamp stays the source of truth and is
    // re-checked periodically (this also keeps multiple tabs in sync).
    intervalRef.current = setInterval(() => {
      if (isSessionExpired()) logout();
      else if (isSessionExpired(Date.now() + SESSION_WARNING_TIME)) setShowWarning(true);
      else setShowWarning(false);
    }, SESSION_CHECK_INTERVAL);
  }, [clearTimers, enabled, logout]);

  useEffect(() => {
    if (!enabled) return undefined;

    localStorage.setItem('userRole', userRole || '');

    if (isSessionExpired()) {
      logout();
      return undefined;
    }

    const debounceTimers = {};
    const handlers = {};

    ACTIVITY_EVENTS.forEach(eventName => {
      handlers[eventName] = () => {
        clearTimeout(debounceTimers[eventName]);
        debounceTimers[eventName] = setTimeout(resetTimer, 100);
      };
      window.addEventListener(eventName, handlers[eventName]);
    });

    const handleVisibilityChange = () => {
      if (document.hidden) return;
      if (isSessionExpired()) logout();
      else resetTimer();
    };

    const handlePageShow = event => {
      // Re-run the idle check when the page is restored from the back/forward
      // cache or reopened, so an expired session never renders the dashboard.
      if (event.persisted && isSessionExpired()) logout();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pageshow', handlePageShow);
    resetTimer();

    return () => {
      clearTimers();
      Object.values(debounceTimers).forEach(timer => clearTimeout(timer));
      ACTIVITY_EVENTS.forEach(eventName => window.removeEventListener(eventName, handlers[eventName]));
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, [clearTimers, enabled, logout, resetTimer, userRole]);

  return (
    <>
      {children}
      {showWarning && (
        <div className="session-warning">
          <div>
            <h4>Session Timeout Warning</h4>
            <p>Your session will expire in less than a minute due to inactivity.</p>
          </div>
          <button type="button" onClick={resetTimer}>Stay Logged In</button>
        </div>
      )}
    </>
  );
}
